import { useQuery } from '@tanstack/react-query';
import { getSystemHealth } from '@/services/api/system-health';
import type { ServiceHealth, ServiceStatus } from '@/types/system-health';
import { Button } from '../components/ui/button';
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    Clock,
    ExternalLink,
    RefreshCw,
    XCircle,
} from 'lucide-react';

// A verificação é um GET de verdade em cada serviço, e não uma leitura do
// estado do container. A diferença é o motivo desta tela existir: em
// 06/09/2026 o Custom Targeting ficou três dias e meio sem atender ninguém
// enquanto o `docker ps` reportava "Up (healthy)".
const ATUALIZA_A_CADA_MS = 30_000;

const ESTILO: Record<ServiceStatus, {
    rotulo: string;
    Icone: typeof CheckCircle2;
    ponto: string;
    texto: string;
    cartao: string;
    chip: string;
}> = {
    online: {
        rotulo: 'Online',
        Icone: CheckCircle2,
        ponto: 'bg-emerald-500',
        texto: 'text-emerald-700',
        cartao: 'border-slate-200',
        chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    lento: {
        rotulo: 'Lento',
        Icone: Clock,
        ponto: 'bg-amber-500',
        texto: 'text-amber-700',
        cartao: 'border-amber-200',
        chip: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    instavel: {
        rotulo: 'Instável',
        Icone: AlertTriangle,
        ponto: 'bg-orange-500',
        texto: 'text-orange-700',
        cartao: 'border-orange-200',
        chip: 'bg-orange-50 text-orange-700 border-orange-200',
    },
    fora: {
        rotulo: 'Fora do ar',
        Icone: XCircle,
        ponto: 'bg-rose-500',
        texto: 'text-rose-700',
        cartao: 'border-rose-300 bg-rose-50/40',
        chip: 'bg-rose-50 text-rose-700 border-rose-200',
    },
};

function formatarHora(iso: string): string {
    const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function CartaoResumo({ rotulo, valor, status }: { rotulo: string; valor: number; status?: ServiceStatus }) {
    const cor = status ? ESTILO[status] : null;
    return (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-2">
                {cor && <span className={`h-2 w-2 rounded-full ${cor.ponto}`} />}
                <span className="text-xs text-slate-500">{rotulo}</span>
            </div>
            <p className={`text-2xl font-semibold mt-1 ${valor > 0 && cor ? cor.texto : 'text-slate-900'}`}>
                {valor}
            </p>
        </div>
    );
}

function LinhaServico({ servico }: { servico: ServiceHealth }) {
    const estilo = ESTILO[servico.status];
    const { Icone } = estilo;

    return (
        <div className={`bg-white rounded-lg border p-4 ${estilo.cartao}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <Icone className={`h-4 w-4 flex-shrink-0 ${estilo.texto}`} />
                        <h3 className="text-sm font-medium text-slate-900 truncate">{servico.name}</h3>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${estilo.chip}`}>
                            {estilo.rotulo}
                        </span>
                    </div>

                    <p className="text-xs text-slate-500 mt-1">{servico.description}</p>

                    {servico.detail && (
                        <p className={`text-xs mt-1.5 ${estilo.texto}`}>{servico.detail}</p>
                    )}

                    <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-slate-400 font-mono">{servico.key}</span>
                        {servico.public_url && (
                            <a
                                href={`https://${servico.public_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
                            >
                                {servico.public_url}
                                <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                    </div>
                </div>

                <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-medium ${estilo.texto}`}>
                        {servico.latency_ms !== null ? `${servico.latency_ms} ms` : '—'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {servico.http_status !== null ? `HTTP ${servico.http_status}` : 'sem resposta'}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function SystemHealth() {
    const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
        queryKey: ['system-health'],
        queryFn: getSystemHealth,
        refetchInterval: ATUALIZA_A_CADA_MS,
        // O valor anterior continua na tela enquanto a próxima verificação
        // roda: piscar a lista inteira a cada 30s tornaria a tela inútil para
        // acompanhar um incidente.
        placeholderData: (anterior) => anterior,
    });

    const comProblema = data?.services.filter((s) => s.status !== 'online') ?? [];

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-start justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900 mb-2">Status dos Serviços</h1>
                        <p className="text-sm text-slate-600">
                            Cada serviço da VPS é consultado de verdade, com uma requisição HTTP
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <Button
                            onClick={() => refetch()}
                            disabled={isFetching}
                            className="bg-slate-900 hover:bg-slate-800 text-white"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 text-white ${isFetching ? 'animate-spin' : ''}`} />
                            {isFetching ? 'Verificando...' : 'Verificar agora'}
                        </Button>
                        {data && (
                            <p className="text-xs text-slate-500 mt-2">
                                Última verificação: {formatarHora(data.checked_at)}
                            </p>
                        )}
                    </div>
                </div>

                {isLoading && (
                    <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                        <div className="inline-block h-8 w-8 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
                        <p className="mt-4 text-sm text-slate-600">Verificando serviços...</p>
                    </div>
                )}

                {isError && (
                    <div className="bg-white rounded-lg border border-rose-200 p-6">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-medium text-slate-900">
                                    Não foi possível verificar os serviços
                                </h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    {(error as any)?.response?.status === 403
                                        ? 'Esta tela é restrita a administradores.'
                                        : 'A própria API que faz a verificação não respondeu — o que já é, por si só, um sinal.'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <CartaoResumo rotulo="Serviços" valor={data.summary.total} />
                            <CartaoResumo rotulo="Online" valor={data.summary.online} status="online" />
                            <CartaoResumo rotulo="Lentos" valor={data.summary.lento} status="lento" />
                            <CartaoResumo rotulo="Instáveis" valor={data.summary.instavel} status="instavel" />
                            <CartaoResumo rotulo="Fora do ar" valor={data.summary.fora} status="fora" />
                        </div>

                        {comProblema.length === 0 ? (
                            <div className="bg-white rounded-lg border border-emerald-200 p-4 mb-6">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                    <p className="text-sm text-slate-700">
                                        Todos os {data.summary.total} serviços responderam normalmente.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg border border-amber-200 p-4 mb-6">
                                <div className="flex items-center gap-2">
                                    <Activity className="h-4 w-4 text-amber-600" />
                                    <p className="text-sm text-slate-700">
                                        <span className="font-medium">{comProblema.length}</span>
                                        {comProblema.length === 1 ? ' serviço precisa' : ' serviços precisam'} de
                                        atenção — {comProblema.map((s) => s.name).join(', ')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* A API já devolve os problemas primeiro. */}
                        <div className="space-y-2">
                            {data.services.map((servico) => (
                                <LinhaServico key={servico.key} servico={servico} />
                            ))}
                        </div>

                        <p className="text-xs text-slate-400 mt-6">
                            Atualiza sozinho a cada 30 segundos. Um serviço aparece como
                            "fora do ar" quando não responde — inclusive quando o container está
                            de pé mas nenhum worker atende.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
